import React, { useState } from "react";
import {
  Popover,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  FormControl,
  IconButton,
  Divider,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  patientApi,
  useAddLiveCallMutation,
  useUpdateLiveCallMutation,
} from "app/store/patient/api/patientApi";
import { showErrorToast, showSuccessToast } from "utils/toast";
import SubmitButton from "components/Button/SubmitButton";
import { Close } from "@mui/icons-material";
import dayjs from "dayjs";

import AddIcCallOutlinedIcon from "@mui/icons-material/AddIcCallOutlined";
import { useDispatch } from "react-redux";
import cn from "clsx";
import FormDatePicker from "components/FormFields/FormDatePicker";

const validationSchema = Yup.object().shape({
  callDate: Yup.string().label("Call date and time").required(),
  status: Yup.string().label("Status").required(),
  summary: Yup.string()
    .when("status", {
      is: "received",
      then: (schema) =>
        schema.required("Summary is required when call is received"),
      otherwise: (schema) => schema.notRequired(),
    })
    .label("Summary"),
});

const PatientLiveCall = ({
  patientId,
  isEdit,
  style,
  isTask,
  taskId,
  onSubmit = () => {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [addLiveCall] = useAddLiveCallMutation();
  const [updateLiveCall] = useUpdateLiveCallMutation();

  const dispatch = useDispatch();

  const handleCall = async (values) => {
    try {
      const { callDate, ...rest } = values;

      const payload = {
        ...rest,
        isTask,
        call_date: callDate,
        patientId: patientId,
        ...(taskId && { taskId }),
      };

      let response;

      if (isEdit) {
        response = await updateLiveCall(payload).unwrap();
      } else {
        response = await addLiveCall(payload).unwrap();
      }

      if (!response?.error) {
        showSuccessToast(response?.message);
        setAnchorEl(null);
        dispatch(patientApi.util.invalidateTags(["patientReminderHistory"]));
      }

      onSubmit();
    } catch (error) {
      showErrorToast(error?.message);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Formik reads initialValues only on mount, and the Popover unmounts its
  // content on close, so each open starts fresh with the current date.
  const getInitialValues = () => ({
    callDate: dayjs(),
    status: "",
    summary: "",
  });

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        className="preferences-edit-button ml-2"
        style={{ ...(style ?? {}) }}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <AddIcCallOutlinedIcon
          fontSize="small"
          className={cn({ "mr-2": !isTask })}
        />
        {!isTask ? `${isEdit ? "Edit" : "Add"} Live Call` : ""}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        disableEnforceFocus={true}
        sx={{ zIndex: 1500 }}
      >
        <Box sx={{ width: 580, height: 549 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "24px",
              py: "24px",
            }}
          >
            <Typography variant="h6">
              {isEdit ? "Edit" : "Add"} Live Call
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />

          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleCall}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Box sx={{ px: 2.5, py: 2.5 }}>
                    <FormControl fullWidth margin="none">
                      <FormDatePicker
                        required
                        name="callDate"
                        label="Call date"
                        maxDate={dayjs(new Date())}
                        submitted={touched.callDate}
                        value={
                          values?.callDate ? dayjs(values.callDate) : dayjs()
                        }
                        hasError={touched.callDate && Boolean(errors.callDate)}
                        errorMessage={touched.callDate && errors.callDate}
                        onChange={(value) => {
                          setFieldValue("callDate", value);
                        }}
                        slotProps={{
                          textField: {
                            required: true,
                            fullWidth: true,
                            margin: "none",
                            error: touched.callDate && Boolean(errors.callDate),
                            helperText: touched.callDate && errors.callDate,
                            inputProps: {
                              readOnly: true,
                            },
                          },
                          popper: {
                            modifiers: [
                              {
                                name: "zIndex",
                                enabled: true,
                                phase: "beforeWrite",
                                fn: ({ state }) => {
                                  state.styles.popper.zIndex = 1500;
                                },
                              },
                            ],
                          },
                        }}
                      />
                    </FormControl>

                    <InputLabel
                      sx={{
                        color: "#757575",
                        mt: 1,
                      }}
                    >
                      Call status
                    </InputLabel>
                    <FormControl
                      component="fieldset"
                      margin="none"
                      error={touched.status && Boolean(errors.status)}
                    >
                      <RadioGroup
                        row
                        name="status"
                        value={values.status}
                        onChange={(event) => {
                          setFieldValue("status", event.target.value);
                        }}
                      >
                        <FormControlLabel
                          value="failed"
                          control={<Radio id="failed" />}
                          label="Not Received"
                        />
                        <FormControlLabel
                          value="received"
                          control={<Radio id="received" />}
                          label="Received"
                        />
                      </RadioGroup>
                      {touched.status && errors.status && (
                        <FormHelperText error>{errors.status}</FormHelperText>
                      )}
                    </FormControl>

                    <InputLabel sx={{ color: "#757575", mt: 1, mb: 1 }}>
                      Add call notes
                    </InputLabel>
                    <TextField
                      name="summary"
                      label="Summary"
                      fullWidth
                      margin="none"
                      multiline
                      rows={8}
                      value={values.summary}
                      onChange={handleChange}
                      onBlur={(e) => {
                        const trimmed = e.target.value.trim();
                        setFieldTouched("summary", true);
                        setFieldValue("summary", trimmed);
                      }}
                      error={touched.summary && Boolean(errors.summary)}
                      helperText={touched.summary && errors.summary}
                      inputProps={{
                        maxLength: 500,
                      }}
                    />

                    <SubmitButton
                      disabled={isSubmitting}
                      sx={{ mt: 2 }}
                      buttonText={`${isEdit ? "Update" : "Add"} Summary`}
                      handleClick={handleSubmit}
                      fullWidth
                    />
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Popover>
    </>
  );
};

export default PatientLiveCall;
